import { useEffect, useMemo, useState } from "react";
import { offset } from "@floating-ui/react";
import ApiDatabase from "../../server";
import UseContext from "../../hooks/useContext";
import StyledInput from "../styledInput/StyledInput";
import DatePicker from "react-datepicker";
import { createDatePickerCustomHeader } from "../datePicker/DatePickerCustomHeader";
import Button from "../button";
import { Calendar, LoopIcon } from "../../assets/icons";
import { dateShortText2 } from "../../utils/Date";

const NavBarActivity = ({
  employeeSelectedActivity,
  setEmployeeSelectedActivity,
  actionSelected,
  setActionSelected,
  dateSelect,
  setDateSelect,
  search,
  setSearch,
}) => {
  const { refreshCompany, companySelected } = UseContext();
  const [employeeList, setEmployeeList] = useState([]);
  const [isWaitingEmployees, setIsWaitingEmployees] = useState(true);
  const renderDatePickerHeader = useMemo(
    () => createDatePickerCustomHeader({ monthsShown: 1 }),
    [],
  );
  const [actionList, setActionList] = useState([
    {
      name: "Ajout de téléphone",
      type: "workerPhoneAdd",
      checked: false,
    },
    {
      name: "Modification du téléphone",
      type: "workerPhoneUpdate",
      checked: false,
    },
    {
      name: "Ajout de pointage",
      type: "timeSheetAdd",
      checked: false,
    },
    {
      name: "Modification de pointage",
      type: "timeSheetUpdate",
      checked: false,
    },
    {
      name: "Suppression de pointage",
      type: "timeSheetDelete",
      checked: false,
    },
    {
      name: "Export de données",
      type: "exportMission",
      checked: false,
    },
    {
      name: "Activation PDNS",
      type: "pdnsCompanyEnable",
      checked: false,
    },
    {
      name: "Désactivation PDNS",
      type: "pdnsCompanyDisable",
      checked: false,
    },
    {
      name: "Activation Heures Dimanche",
      type: "sundayHoursCompanyEnable",
      checked: false,
    },
    {
      name: "Désactivation Heures Dimanche",
      type: "sundayHoursCompanyDisable",
      checked: false,
    },
  ]);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        setIsWaitingEmployees(true);
        const getEmployeesPromise = () =>
          new Promise((resolve, reject) => {
            ApiDatabase.getTeams(
              {},
              (data) => resolve(data),
              (err) => reject(err),
            );
          });

        const data = await getEmployeesPromise();

        if (data.teams?.length > 0) {
          setEmployeeList(
            data.teams.map((team) => ({
              idEmployee: team._id,
              name: team.firstname + " " + team.lastname,
              checked: false,
            })),
          );
        }
      } catch (err) {
        console.log(err);
      } finally {
        setIsWaitingEmployees(false);
      }
    };

    fetchEmployees();
  }, [refreshCompany, companySelected?.idCompany]);

  const updateEmployeesList = (updatedEmployee) => {
    const updatedEmployees = [];
    for (let i = 0; i < employeeList.length; i++) {
      for (let j = 0; j < updatedEmployee.length; j++) {
        if (employeeList[i].idEmployee === updatedEmployee[j].value) {
          updatedEmployees.push({
            ...employeeList[i],
            checked: updatedEmployee[j].checked,
          });
        }
      }
    }
    setEmployeeList(updatedEmployees);
    const selectedEmployeeStatus = updatedEmployees.filter(
      (missionStatus) => missionStatus.checked,
    );

    setEmployeeSelectedActivity(selectedEmployeeStatus);
  };

  const updateActionList = (updatedAction) => {
    const updatedActions = [];

    for (let i = 0; i < actionList.length; i++) {
      for (let j = 0; j < updatedAction.length; j++) {
        if (actionList[i].type === updatedAction[j].value) {
          updatedActions.push({
            ...actionList[i],
            checked: updatedAction[j].checked,
          });
        }
      }
    }

    setActionList(updatedActions);

    const selectedInterimAgencies = updatedActions.filter(
      (interimAgency) => interimAgency.checked,
    );

    setActionSelected(selectedInterimAgencies);
  };

  return (
    <nav className={"w-full flex justify-between items-center min-h-[45px]"}>
      <div className={"flex gap-2 items-center text-sm"}>
        <div className={"max-w-[150px] text-sm"}>
          <StyledInput
            type={"selectMultipleCheckbox"}
            value={
              actionSelected.length > 0
                ? actionSelected.map((action) => action.name).join(", ")
                : "Action"
            }
            list={actionList.map((action) => {
              return {
                value: action.type,
                label: action.name,
                checked: action.checked,
              };
            })}
            onChange={(updatedAction) => updateActionList(updatedAction)}
            isSearchable={true}
          />
        </div>
        {!isWaitingEmployees ? (
          <>
            {employeeList.length > 1 && (
              <div className={"max-w-[150px] text-sm"}>
                <StyledInput
                  type={"selectMultipleCheckbox"}
                  value={
                    employeeSelectedActivity.length > 0
                      ? employeeSelectedActivity
                          .map((employee) => employee.name)
                          .join(", ")
                      : "Utilisateur"
                  }
                  list={employeeList.map((employee) => {
                    return {
                      value: employee.idEmployee,
                      label: employee.name,
                      checked: employee.checked,
                    };
                  })}
                  onChange={(updatedEmployee) =>
                    updateEmployeesList(updatedEmployee)
                  }
                  isSearchable={true}
                />
              </div>
            )}
          </>
        ) : (
          <div className={"h-12 bg-gray-200 rounded"}></div>
        )}
        <div>
          <DatePicker
            dateFormat="dd/MM/yyyy"
            selected={dateSelect}
            onChange={(date) => setDateSelect(date)}
            locale="fr"
            peekNextMonth
            showMonthDropdown={false}
            showYearDropdown={false}
            renderCustomHeader={renderDatePickerHeader}
            dropdownMode="select"
            popperPlacement="bottom"
            popperModifiers={[offset({ mainAxis: -2 })]}
            className="w-full h-9 pl-3 pr-8 ft-b border rounded-lg text-center"
            customInput={
              <Button
                color={dateSelect ? "PRIMARY" : "SECONDARY"}
                size={"ICON"}
                name="header_activity_date_calendar"
                title={
                  dateSelect
                    ? `${dateShortText2(dateSelect)} — Cliquer pour modifier la date`
                    : "Choisir une date"
                }
              >
                <Calendar
                  wh={16}
                  color={dateSelect ? "#FFFFFF" : "#374151"}
                />
              </Button>
            }
            /*includeDates={dateList}*/
          />
        </div>
        <div className={"w-[213px] text-sm"}>
          <StyledInput
            type={"text"}
            placeholder={"Rechercher un objet"}
            imgFront={<LoopIcon wh={16} color={"#6B7280"} />}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
            }}
          />
        </div>
      </div>
    </nav>
  );
};

export default NavBarActivity;
